/**
 * ReferralPage
 * 레퍼럴 관리 페이지 - 추천인 코드, 수익, 출금 관리
 */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { getReferralStats, requestWithdrawal, type ReferralStats } from '../../services/referral';
import { generateReferralLink, copyReferralLink, copyReferralCode } from '../../utils/referral';
import { tokens } from '../../design-system/tokens';

// 회원 등급 표시 텍스트 매핑
const getGradeDisplayText = (grade: string): string => {
  const gradeMap: Record<string, string> = {
    'regular': '정회원',
    'special': '특별회원',
    'admin': '관리자',
    'guest': '일반회원'
  };
  return gradeMap[grade] || grade;
};

export const ReferralPage: React.FC = () => {
  const { user, userInfo } = useAuth();
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 복사 상태
  const [linkCopied, setLinkCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  // 출금 폼 상태
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [withdrawalLoading, setWithdrawalLoading] = useState(false);
  const [withdrawalSuccess, setWithdrawalSuccess] = useState(false);

  // 레퍼럴 통계 로드
  useEffect(() => {
    const loadStats = async () => {
      if (!user) return;

      try {
        setLoading(true);
        const data = await getReferralStats(user.uid);
        setStats(data);
      } catch (err: any) {
        console.error('Failed to load referral stats:', err);
        setError('레퍼럴 통계를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [user]);

  // 레퍼럴 링크 복사
  const handleCopyLink = async () => {
    if (!userInfo?.referralCode) return;

    const success = await copyReferralLink(userInfo.referralCode);
    if (success) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } else {
      setError('링크 복사에 실패했습니다. 다시 시도해주세요.');
      setTimeout(() => setError(null), 3000);
    }
  };

  // 레퍼럴 코드 복사
  const handleCopyCode = async () => {
    if (!userInfo?.referralCode) return;

    const success = await copyReferralCode(userInfo.referralCode);
    if (success) {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } else {
      setError('코드 복사에 실패했습니다. 다시 시도해주세요.');
      setTimeout(() => setError(null), 3000);
    }
  };

  // 전액 출금 버튼 핸들러
  const handleMaxAmount = () => {
    if (stats && stats.availableBalance >= 10000) {
      setWithdrawalAmount(stats.availableBalance.toString());
    }
  };

  // 출금 신청
  const handleWithdrawalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setWithdrawalSuccess(false);

    const amount = parseInt(withdrawalAmount);

    if (amount < 10000) {
      setError('최소 출금 금액은 10,000원입니다.');
      return;
    }

    if (!stats || amount > stats.availableBalance) {
      setError('출금 가능 금액이 부족합니다.');
      return;
    }

    if (!bankName || !accountNumber || !accountHolder) {
      setError('계좌 정보를 모두 입력해주세요.');
      return;
    }

    try {
      setWithdrawalLoading(true);
      await requestWithdrawal(amount, {
        bankName,
        accountNumber,
        accountHolder
      });

      setWithdrawalSuccess(true);
      // 폼 초기화
      setWithdrawalAmount('');
      setBankName('');
      setAccountNumber('');
      setAccountHolder('');

      // 통계 새로고침
      if (user) {
        const updatedStats = await getReferralStats(user.uid);
        setStats(updatedStats);
      }
    } catch (err: any) {
      setError(err.message || '출금 신청 중 오류가 발생했습니다.');
    } finally {
      setWithdrawalLoading(false);
    }
  };

  if (loading) {
    return (
      <Content>
        <LoadingMessage>레퍼럴 정보를 불러오는 중...</LoadingMessage>
      </Content>
    );
  }

  const referralLink = userInfo?.referralCode ? generateReferralLink(userInfo.referralCode) : '';

  return (
    <Content>
          <Title>레퍼럴 관리</Title>
          {userInfo?.memberGrade && (
            <GradeBadge $grade={userInfo.memberGrade}>
              {getGradeDisplayText(userInfo.memberGrade)}
            </GradeBadge>
          )}
          <Subtitle>간단한 홍보로 수익 활동에 참여해 주세요</Subtitle>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {withdrawalSuccess && <SuccessMessage>출금 신청이 완료되었습니다!</SuccessMessage>}

          {/* 추천 링크 & 코드 */}
          <Section>
            <SectionTitle>나의 추천 정보</SectionTitle>

            <InfoBox>
              <Label>추천 코드</Label>
              <CodeDisplay>{userInfo?.referralCode || '-'}</CodeDisplay>
              <CopyButton
                onClick={handleCopyCode}
                aria-label={codeCopied ? '추천 코드 복사 완료' : '추천 코드 클립보드에 복사'}
              >
                {codeCopied ? '✓ 복사 완료!' : '📋 코드 복사'}
              </CopyButton>
            </InfoBox>

            <InfoBox>
              <Label>추천 링크</Label>
              <LinkDisplay>{referralLink}</LinkDisplay>
              <CopyButton
                onClick={handleCopyLink}
                aria-label={linkCopied ? '추천 링크 복사 완료' : '추천 링크 클립보드에 복사'}
              >
                {linkCopied ? '✓ 복사 완료!' : '📋 링크 복사'}
              </CopyButton>
            </InfoBox>
          </Section>

          {/* 수익 통계 */}
          <Section>
            <SectionTitle>수익 현황</SectionTitle>

            <StatsGrid>
              <StatCard>
                <StatLabel>총 수익</StatLabel>
                <StatValue>{stats?.totalEarnings?.toLocaleString() || 0}원</StatValue>
              </StatCard>

              <StatCard>
                <StatLabel>출금 가능 금액</StatLabel>
                <StatValue $highlight>{stats?.availableBalance?.toLocaleString() || 0}원</StatValue>
              </StatCard>

              <StatCard>
                <StatLabel>출금 대기</StatLabel>
                <StatValue>{stats?.pendingWithdrawals?.toLocaleString() || 0}원</StatValue>
              </StatCard>

              <StatCard>
                <StatLabel>추천 회원 수</StatLabel>
                <StatValue>{stats?.referredUsersCount || 0}명</StatValue>
              </StatCard>
            </StatsGrid>
          </Section>

          {/* 출금 신청 */}
          <Section>
            <SectionTitle>출금 신청</SectionTitle>

            <WithdrawalForm onSubmit={handleWithdrawalSubmit}>
              <FormGroup>
                <FormLabel>출금 금액 (최소 10,000원)</FormLabel>
                <AmountInputWrapper>
                  <FormInput
                    type="number"
                    value={withdrawalAmount}
                    onChange={(e) => setWithdrawalAmount(e.target.value)}
                    placeholder="10000"
                    min="10000"
                    required
                  />
                  <MaxButton
                    type="button"
                    onClick={handleMaxAmount}
                    disabled={!stats || stats.availableBalance < 10000}
                  >
                    전액
                  </MaxButton>
                </AmountInputWrapper>
              </FormGroup>

              <FormGroup>
                <FormLabel>은행명</FormLabel>
                <FormInput
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  placeholder="예: 국민은행"
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>계좌번호</FormLabel>
                <FormInput
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="숫자만 입력"
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>예금주</FormLabel>
                <FormInput
                  type="text"
                  value={accountHolder}
                  onChange={(e) => setAccountHolder(e.target.value)}
                  placeholder="예금주명"
                  required
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={withdrawalLoading}>
                {withdrawalLoading ? '처리 중...' : '출금 신청'}
              </SubmitButton>
            </WithdrawalForm>
          </Section>

          {/* 추천 회원 목록 */}
          {stats && stats.referredUsers.length > 0 && (
            <Section>
              <SectionTitle>추천 회원 내역</SectionTitle>

              <ReferredUsersTable>
                <thead>
                  <tr>
                    <th>회원명</th>
                    <th>이메일</th>
                    <th>결제일</th>
                    <th>총 결제금액</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.referredUsers.map((referred) => (
                    <tr key={referred.uid}>
                      <td>{referred.displayName}</td>
                      <td>{referred.email}</td>
                      <td>{referred.joinedAt ? new Date(referred.joinedAt).toLocaleDateString('ko-KR') : '-'}</td>
                      <td>{referred.totalPayments.toLocaleString()}원</td>
                    </tr>
                  ))}
                </tbody>
              </ReferredUsersTable>
            </Section>
          )}
    </Content>
  );
};

// Styled Components
const Content = styled.div`
  padding: ${tokens.spacing[8]} ${tokens.spacing[6]};

  @media (max-width: ${tokens.breakpoints.md}) {
    padding: ${tokens.spacing[6]} ${tokens.spacing[5]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[5]} ${tokens.spacing[4]};
  }
`;

const Title = styled.h1`
  font-size: ${tokens.typography.fontSize['3xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[800]};
  text-align: center;
  margin-bottom: ${tokens.spacing[2]};
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.md}) {
    font-size: ${tokens.typography.fontSize['2xl']};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xl};
  }
`;

const GradeBadge = styled.div<{ $grade: string }>`
  display: block;
  width: fit-content;
  margin: ${tokens.spacing[3]} auto ${tokens.spacing[2]};
  padding: ${tokens.spacing[2]} ${tokens.spacing[5]};
  border-radius: ${tokens.borderRadius.full};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.semibold};
  text-align: center;
  word-break: keep-all;

  ${props => {
    switch (props.$grade) {
      case 'special':
        return `
          background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
          color: ${tokens.colors.neutral[800]};
          box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
        `;
      case 'admin':
        return `
          background: ${tokens.colors.neutral[800]};
          color: ${tokens.colors.neutral[0]};
          box-shadow: ${tokens.shadows.md};
        `;
      case 'regular':
      default:
        return `
          background: ${tokens.colors.primary[500]};
          color: ${tokens.colors.neutral[0]};
          box-shadow: 0 2px 8px ${tokens.colors.primary[500]}33;
        `;
    }
  }}

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xs};
    padding: ${tokens.spacing[1]} ${tokens.spacing[4]};
  }
`;

const Subtitle = styled.p`
  font-size: ${tokens.typography.fontSize.base};
  color: ${tokens.colors.neutral[600]};
  text-align: center;
  margin-bottom: ${tokens.spacing[8]};
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.md}) {
    font-size: ${tokens.typography.fontSize.sm};
    margin-bottom: ${tokens.spacing[6]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.sm};
    margin-bottom: ${tokens.spacing[5]};
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${tokens.spacing[12]};
  color: ${tokens.colors.neutral[500]};
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[8]};
    font-size: ${tokens.typography.fontSize.base};
  }
`;

const ErrorMessage = styled.div`
  background: ${tokens.colors.semantic.error}15;
  color: ${tokens.colors.semantic.error};
  padding: ${tokens.spacing[4]};
  border-radius: ${tokens.borderRadius.md};
  margin-bottom: ${tokens.spacing[6]};
  text-align: center;
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[3]};
    font-size: ${tokens.typography.fontSize.sm};
  }
`;

const SuccessMessage = styled.div`
  background: ${tokens.colors.semantic.success}15;
  color: ${tokens.colors.semantic.success};
  padding: ${tokens.spacing[4]};
  border-radius: ${tokens.borderRadius.md};
  margin-bottom: ${tokens.spacing[6]};
  text-align: center;
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[3]};
    font-size: ${tokens.typography.fontSize.sm};
  }
`;

const Section = styled.section`
  margin-bottom: ${tokens.spacing[8]};

  @media (max-width: ${tokens.breakpoints.sm}) {
    margin-bottom: ${tokens.spacing[6]};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${tokens.typography.fontSize.xl};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
  margin-bottom: ${tokens.spacing[4]};
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.md}) {
    font-size: ${tokens.typography.fontSize.lg};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.base};
    margin-bottom: ${tokens.spacing[3]};
  }
`;

const InfoBox = styled.div`
  background: ${tokens.colors.neutral[50]};
  padding: ${tokens.spacing[4]};
  border-radius: ${tokens.borderRadius.md};
  margin-bottom: ${tokens.spacing[4]};

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[3]};
  }
`;

const Label = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[600]};
  margin-bottom: ${tokens.spacing[2]};
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xs};
  }
`;

const CodeDisplay = styled.div`
  font-size: ${tokens.typography.fontSize['2xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.primary[500]};
  margin-bottom: ${tokens.spacing[3]};
  font-family: 'Courier New', monospace;

  @media (max-width: ${tokens.breakpoints.md}) {
    font-size: ${tokens.typography.fontSize.xl};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.lg};
  }
`;

const LinkDisplay = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[700]};
  margin-bottom: ${tokens.spacing[3]};
  word-break: break-all;
  background: ${tokens.colors.neutral[0]};
  padding: ${tokens.spacing[2]};
  border-radius: ${tokens.borderRadius.sm};

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xs};
  }
`;

const CopyButton = styled.button`
  background: ${tokens.colors.primary[500]};
  color: ${tokens.colors.neutral[0]};
  border: none;
  padding: ${tokens.spacing[2]} ${tokens.spacing[4]};
  border-radius: ${tokens.borderRadius.sm};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${tokens.animation.duration.base} ${tokens.animation.easing.ease};

  &:hover {
    background: ${tokens.colors.primary[600]};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xs};
    padding: ${tokens.spacing[2]} ${tokens.spacing[3]};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${tokens.spacing[4]};

  @media (max-width: ${tokens.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${tokens.spacing[3]};
  }
`;

const StatCard = styled.div`
  background: ${tokens.colors.neutral[50]};
  padding: ${tokens.spacing[4]};
  border-radius: ${tokens.borderRadius.md};
  text-align: center;

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[3]};
  }
`;

const StatLabel = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[600]};
  margin-bottom: ${tokens.spacing[2]};
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xs};
  }
`;

const StatValue = styled.div<{ $highlight?: boolean }>`
  font-size: ${tokens.typography.fontSize['2xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${props => props.$highlight ? tokens.colors.primary[500] : tokens.colors.neutral[800]};

  @media (max-width: ${tokens.breakpoints.md}) {
    font-size: ${tokens.typography.fontSize.xl};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.lg};
  }
`;

const WithdrawalForm = styled.form`
  background: ${tokens.colors.neutral[50]};
  padding: ${tokens.spacing[6]};
  border-radius: ${tokens.borderRadius.md};

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[4]};
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${tokens.spacing[4]};

  @media (max-width: ${tokens.breakpoints.sm}) {
    margin-bottom: ${tokens.spacing[3]};
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[700]};
  margin-bottom: ${tokens.spacing[2]};
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xs};
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: ${tokens.spacing[3]};
  border: 1px solid ${tokens.colors.neutral[300]};
  border-radius: ${tokens.borderRadius.sm};
  font-size: ${tokens.typography.fontSize.base};

  &:focus {
    outline: none;
    border-color: ${tokens.colors.primary[500]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[2]};
    font-size: ${tokens.typography.fontSize.sm};
  }
`;

const AmountInputWrapper = styled.div`
  display: flex;
  gap: ${tokens.spacing[2]};
  align-items: center;
`;

const MaxButton = styled.button`
  background: ${tokens.colors.neutral[100]};
  color: ${tokens.colors.neutral[700]};
  border: 1px solid ${tokens.colors.neutral[300]};
  padding: ${tokens.spacing[3]} ${tokens.spacing[4]};
  border-radius: ${tokens.borderRadius.sm};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${tokens.animation.duration.base} ${tokens.animation.easing.ease};
  white-space: nowrap;
  min-width: 60px;

  &:hover:not(:disabled) {
    background: ${tokens.colors.neutral[200]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[2]} ${tokens.spacing[3]};
    font-size: ${tokens.typography.fontSize.xs};
    min-width: 50px;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: ${tokens.colors.primary[500]};
  color: ${tokens.colors.neutral[0]};
  border: none;
  padding: ${tokens.spacing[4]};
  border-radius: ${tokens.borderRadius.md};
  font-size: ${tokens.typography.fontSize.base};
  font-weight: ${tokens.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${tokens.animation.duration.base} ${tokens.animation.easing.ease};

  &:hover:not(:disabled) {
    background: ${tokens.colors.primary[600]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[3]};
    font-size: ${tokens.typography.fontSize.sm};
  }
`;

const ReferredUsersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${tokens.colors.neutral[0]};
  border-radius: ${tokens.borderRadius.md};
  overflow: hidden;
  display: block;
  overflow-x: auto;

  th {
    background: ${tokens.colors.neutral[100]};
    padding: ${tokens.spacing[3]};
    text-align: left;
    font-size: ${tokens.typography.fontSize.sm};
    font-weight: ${tokens.typography.fontWeight.semibold};
    color: ${tokens.colors.neutral[700]};
    word-break: keep-all;
    white-space: nowrap;
  }

  td {
    padding: ${tokens.spacing[3]};
    border-top: 1px solid ${tokens.colors.neutral[200]};
    font-size: ${tokens.typography.fontSize.sm};
    color: ${tokens.colors.neutral[700]};
    white-space: nowrap;
  }

  tr:hover td {
    background: ${tokens.colors.neutral[50]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    th {
      padding: ${tokens.spacing[2]};
      font-size: ${tokens.typography.fontSize.xs};
    }

    td {
      padding: ${tokens.spacing[2]};
      font-size: ${tokens.typography.fontSize.xs};
    }
  }
`;
